import { Component, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { merge, Observable, Subject, map, mergeMap, toArray, takeUntil } from 'rxjs';
import { ReviewsService } from '../reviews.service';
import { Chart, ChartOptions } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { PatientData } from 'src/app/models/ccm/profile.model';
import { Review } from 'src/app/models/ccm/review.model';
import { ChartModule } from 'primeng/chart';
import { CommonModule } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ValidationUtils } from 'src/app/utils/validation.utils';
import { LoggingService } from '../../../../services/logging.service';
import { ToastNotificationService } from '../../../../services/toast-notification.service';

Chart.register(zoomPlugin);

export interface ChartDataset {
  label: string;
  backgroundColor: string;
  borderColor: string;
  pointBackgroundColor: string;
  pointBorderColor: string;
  data: (number | null)[];
}

export interface ChartConfig {
  labels: string[];
  datasets: ChartDataset[];
}

interface BloodPressureReading {
  type: string;
  data: {
    recorded_date: string;
    value: number;
  };
}
interface BloodGlucoseReading {
  type: string;
  data: {
    recorded_date: string;
    value: number;
  };
}

interface BloodPressureObservation {
  type: string;
  unit: string;
  value: null;
  method: string;
  deviceId: null;
  warnings: Array<{ cleared: boolean; clearedBy: null; description: string; }>;
  patientId: number;
  deviceName: null;
  recordedBy: string;
  recordedDate: string;
  observationId: number;
  systolicValue: number;
  diastolicValue: number;
}

interface BloodPressureRow {
  data: BloodPressureObservation;
}

interface BloodPressureResponse {
  Observations: BloodPressureRow[];
}
@Component({
  selector: 'app-patient-charts',
  standalone: true,
  templateUrl: './patient-charts.component.html',
  styleUrl: './patient-charts.component.scss',
  imports: [
    CommonModule,
    ChartModule,
    ProgressSpinnerModule
  ]
})
export class PatientChartsComponent implements OnInit, OnDestroy, OnChanges {
  charts: {
    bp: ChartConfig;
    glucose: ChartConfig;
  } | undefined;

  @Input() chartData: ChartConfig | null = null;
  @Input() chartTitle: string = '';
  @Input() loading: boolean = false;
  @Input() noDataMessage: string = ValidationUtils.getNoDataMessage('chart data');

  @Input() review: Review | null = null;
  @Input() isDashboardReport: boolean = false;

  @Output() readonly chartDataAvailable = new EventEmitter<boolean>();

  chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      zoom: {
        pan: { enabled: true, mode: 'x' },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: 'x',
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed.y ?? context.parsed;
            const seriesLabel = context.dataset.label ? `${context.dataset.label}: ` : '';
            return `${seriesLabel}${value} (${label})`;
          },
        },
      },
      legend: {
        display: true,
        position: 'top',
      },
    },
  };
  reviewModeLoading = false;
  private readonly destroyed$ = new Subject<void>();
  private readonly cancelChartLoad$ = new Subject<void>();
  private loadedPatientKey: string | null = null;
  private pendingChartRequests = 0;

  constructor(
    private readonly getReviews: ReviewsService,
    private readonly loggingService: LoggingService,
    private readonly toastNotificationService: ToastNotificationService
  ) {
    this.charts = {
      bp: { datasets: [], labels: [] },
      glucose: { datasets: [], labels: [] }
    };
  }

  ngOnInit(): void {
    this.loadCharts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['review']) {
      return;
    }
    this.loadCharts();
  }

  private loadCharts(): void {
    if (this.chartData != null) {
      return;
    }
    const pd: PatientData | undefined = this.review?.patient_data;
    const ouid = pd?.orgUuid;
    const pid = pd?.patientId;
    const patientKey = ouid && pid ? `${ouid}|${pid}` : null;
    if (patientKey !== null && patientKey === this.loadedPatientKey) {
      return;
    }

    this.cancelChartLoad$.next();
    this.loadedPatientKey = patientKey;
    this.charts = {
      bp: { datasets: [], labels: [] },
      glucose: { datasets: [], labels: [] }
    };
    this.emitChartDataAvailability();

    if (!ouid || !pid) {
      this.reviewModeLoading = false;
      return;
    }
    this.reviewModeLoading = true;
    this.pendingChartRequests = 2;
    const cancel$ = merge(this.cancelChartLoad$, this.destroyed$);

    this.getBloodGlucoseReadings(ouid, pid).pipe(
      toArray(),
      map(readingsArray => this.processReadingsAVG(readingsArray)),
      takeUntil(cancel$)
    ).subscribe({
      next: chartData => {
        if (this.charts && chartData) {
          this.charts.glucose = { labels: chartData.labels, datasets: chartData.datasets };
        }
        this.emitChartDataAvailability();
        this.completeChartRequest();
      },
      error: err => {
        this.loggingService.logException(
          new Error(`Failed to load blood glucose readings for patient ${pid}: ${err}`),
          2
        );
        this.toastNotificationService.showError('Unable to load blood glucose readings. Please try again later.');
        this.emitChartDataAvailability();
        this.completeChartRequest();
      }
    });

    this.getBloodPressureReadings(ouid, pid).pipe(
      toArray(),
      map(readingsArray => this.processReadingsAVG(readingsArray)),
      takeUntil(cancel$)
    ).subscribe({
      next: chartData => {
        if (this.charts && chartData) {
          this.charts.bp = { labels: chartData.labels, datasets: chartData.datasets };
        }
        this.emitChartDataAvailability();
        this.completeChartRequest();
      },
      error: err => {
        this.loggingService.logException(
          new Error(`Failed to load blood pressure readings for patient ${pid}: ${err}`),
          2
        );
        this.toastNotificationService.showError('Unable to load blood pressure readings. Please try again later.');
        this.emitChartDataAvailability();
        this.completeChartRequest();
      }
    });
  }

  private completeChartRequest(): void {
    this.pendingChartRequests--;
    if (this.pendingChartRequests <= 0) {
      this.reviewModeLoading = false;
    }
  }

  private emitChartDataAvailability(): void {
    this.chartDataAvailable.emit(!!(this.charts?.bp?.datasets?.length || this.charts?.glucose?.datasets?.length));
  }

  getBloodPressureReadings(ouid: string, pid: string): Observable<BloodPressureReading> {
    return this.getReviews.getObservations(ouid, pid, 'bloodPressure', '').pipe(
      // Assuming each response contains an array of observations
      mergeMap(response => (<BloodPressureResponse>response).Observations),
      map(observation  => [
        {
          type: 'systolic',
          data: {
            recorded_date: (<BloodPressureRow>observation).data.recordedDate,
            value: (<BloodPressureRow>observation).data.systolicValue,
          },
        },
        {
          type: 'diastolic',
          data: {
            recorded_date: (<BloodPressureRow>observation).data.recordedDate,
            value: (<BloodPressureRow>observation).data.diastolicValue,
          },
        },
      ]),
      // Flatten the array of arrays into a single array
      mergeMap(readings => readings)
    );
  }

  getBloodGlucoseReadings(ouid: string, pid: string): Observable<BloodGlucoseReading> {
    return this.getReviews.getObservations(ouid, pid, 'bloodSugar', '').pipe(
      // Assuming each response contains an array of observations
      mergeMap(response => (<{Observations: any }>response).Observations),
      map(observation  => [
          {
            type: 'glucose',
            data: {
              recorded_date: (<{data:any}>observation).data.recordedDate,
              value: (<{data:any}>observation).data.value,
            },
          },
        ]
      ),
      // Flatten the array of arrays into a single array
      mergeMap(readings => readings)
    );

  }

  processReadingsAVG(readings: BloodPressureReading[]) {
    // Extracting the month and year from the first reading for simplicity
    // Assumes all readings are from the same month and year
    if (readings.length <= 0)
      return null;

    const sampleDate = new Date(readings[0].data.recorded_date);
    const year = sampleDate.getFullYear();
    const month = sampleDate.getMonth();

    // Getting all days of the month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const labels = Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString());

    const uniqueTypes = [...new Set(readings.map(r => r.type))];

    const datasets = uniqueTypes.map(type => {
      const typeReadings = readings.filter(r => r.type === type);
      const dailyAverages = labels.map(day => {
        const dayReadings = typeReadings.filter(r => new Date(r.data.recorded_date).getDate() === Number.parseInt(day));
        const average = dayReadings.reduce((acc, curr) => acc + curr.data.value, 0) / dayReadings.length || null;
        return average;
      });

      // Defining the structure for color settings
      interface ColorSettings {
        backgroundColor: string;
        borderColor: string;
      }

      // Extending the color mapping to include an index signature
      interface ColorMapping {
        [type: string]: ColorSettings;
      }

      // Color mapping for specific types
      const colorMapping: ColorMapping = {
        systolic: {
          backgroundColor: '#E97A2E', // Example: Pink for systolic
          borderColor: '#E97A2E'
        },
        diastolic: {
          backgroundColor: '#3fab9f', // Example: Blue for diastolic
          borderColor: '#3fab9f'
        },
        glucose: {
          backgroundColor: '#df2964', // Example: Pink for systolic
          borderColor: '#df2964'
        },
      };

      // Use color mapping for specific types
      const colors = colorMapping[type] || {
        backgroundColor: 'rgba(220, 220, 220, 0.2)', // Default color if type not found in mapping
        borderColor: 'rgba(220, 220, 220, 1)'
      };

      return {
        label: type,
        data: dailyAverages,
        ...colors,
        fill: false,
        pointBackgroundColor: colors.borderColor,
        pointBorderColor: '#fff'
      };
    });

    return { labels, datasets };
  }

  ngOnDestroy(): void {
    this.cancelChartLoad$.next();
    this.cancelChartLoad$.complete();
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
